import { EmailCaptureExperimentV2PayloadParams } from './emailCaptureV2/emailCaptureExperimentV2Types'
import {
  AvailabilityPreferenceV3ExperimentPayloadParams,
  CustomPreferencesExperimentPayloadParams,
  GenericExperimentPayloadParams,
  MultiplePrimaryNeedsPayloadParams,
  ProgramAwareTriageExperimentPayloadParams,
  S4CHomebaseHeaderQ12025PayloadParams,
  UrgencyToBookExperimentPayloadParams,
} from './searchForCareExperimentTypes'
import { ExperimentPayload, ExperimentUUIDs } from './types'
import { PreferencesEntrypointMapping } from '../memberPreferences/types'

export const generateLatestMatchesExperimentPayload = ({
  arm,
  client_lyra_id,
  device_uuid,
}: {
  arm: string
  client_lyra_id: string
  device_uuid: string
}): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.LatestMatchesUUID,
  deviceUUID: device_uuid,
  experiments: {
    [ExperimentUUIDs.LatestMatchesUUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            client_lyra_id,
          },
        },
      ],
    },
  },
})

export const generateTriageSummaryExperimentPayload = ({
  arm,
  client_lyra_id,
  device_uuid,
  customer_name,
}: {
  arm: 'control' | 'experiment'
  client_lyra_id: string
  device_uuid: string
  customer_name?: string
}): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.TriageSummaryUUID,
  deviceUUID: device_uuid,
  experiments: {
    [ExperimentUUIDs.TriageSummaryUUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            client_lyra_id,
            device_uuid,
            customer_name,
          },
        },
      ],
    },
  },
})

export const generateAvailabiltyPreferenceV3ExperimentPayload = ({
  arm,
  client_lyra_id,
  device_uuid,
  search_id,
  page_location,
}: AvailabilityPreferenceV3ExperimentPayloadParams): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.AvailabilityPreferenceV3UUID,
  deviceUUID: device_uuid,
  experiments: {
    [ExperimentUUIDs.AvailabilityPreferenceV3UUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            client_lyra_id,
            search_id,
            page_location,
          },
        },
      ],
    },
  },
})

// Function to determine which page the user is coming from to view member preferences page
export const getPreferencesEntrypoint = ({
  isComingFromConnectedCalendarPage,
  isComingFromProviderResultsPage,
}: {
  isComingFromConnectedCalendarPage: boolean
  isComingFromProviderResultsPage: boolean
}) => {
  return isComingFromConnectedCalendarPage
    ? PreferencesEntrypointMapping.CONNECTED_CALENDAR
    : isComingFromProviderResultsPage
    ? PreferencesEntrypointMapping.PROVIDER_RESULTS
    : PreferencesEntrypointMapping.RECOMMENDATIONS_EXPLAINED
}

export const generateUrgencyToBookExperimentPayload = ({
  arm,
  client_lyra_id,
  device_uuid,
  search_id,
  matcher_request_id,
  provider_id,
  page_location,
}: UrgencyToBookExperimentPayloadParams): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.UrgencyToBookUUID,
  deviceUUID: device_uuid,
  experiments: {
    [ExperimentUUIDs.UrgencyToBookUUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            client_lyra_id,
            search_id,
            provider_id,
            page_location,
            matcher_request_id,
          },
        },
      ],
    },
  },
})

export const generateCustomPreferencesExperimentPayload = ({
  arm,
  client_lyra_id,
  device_uuid,
}: CustomPreferencesExperimentPayloadParams): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.CustomPreferencesExperimentUUID,
  deviceUUID: device_uuid,
  experiments: {
    [ExperimentUUIDs.CustomPreferencesExperimentUUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            client_lyra_id,
          },
        },
      ],
    },
  },
})

export const generateS4CHomebaseHeaderQ12025ExperimentPayload = ({
  arm,
  action,
  client_lyra_id,
  search_id,
  triage_need,
  toolkit_topic,
  device_uuid,
  patient,
  has_started_search_for_self,
}: S4CHomebaseHeaderQ12025PayloadParams): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.S4CHomebaseHeaderQ12025UUID,
  deviceUUID: device_uuid,
  experiments: {
    [ExperimentUUIDs.S4CHomebaseHeaderQ12025UUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            client_lyra_id,
            action,
            search_id,
            triage_need,
            toolkit_topic,
            patient,
            has_started_search_for_self,
          },
        },
      ],
    },
  },
})

export const generateMultiplePrimaryNeedsExperimentPayload = ({
  arm,
  client_lyra_id,
  search_id,
  device_uuid,
  customer_name,
  origin,
}: MultiplePrimaryNeedsPayloadParams): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.MultiplePrimaryNeedsUUID,
  client_lyra_id,
  deviceUUID: device_uuid,
  experiments: {
    [ExperimentUUIDs.MultiplePrimaryNeedsUUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            client_lyra_id,
            device_uuid,
            customer_name,
            search_id,
            origin,
          },
        },
      ],
    },
  },
})

export const isEmailCapturePageExperimentV2InControlGroup = (flagValue: string) => {
  return flagValue === 'Control'
}

export const generateEmailCapturePageExperimentV2Payload = ({
  arm,
  deviceUUID,
  customerName,
  productType,
  description,
  metric,
}: EmailCaptureExperimentV2PayloadParams): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.EmailCaptureExperimentV2UUID,
  deviceUUID,
  experiments: {
    [ExperimentUUIDs.EmailCaptureExperimentV2UUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            customer_name: customerName,
            customer_product_type: productType,
            description,
            device_uuid: deviceUUID,
            metric,
          },
        },
      ],
    },
  },
})

export const generateProgramAwareTriageExperimentPayload = ({
  client_lyra_id,
  device_uuid,
  selectedToolkitTopic,
  arm,
}: ProgramAwareTriageExperimentPayloadParams): ExperimentPayload => ({
  experimentUUID: ExperimentUUIDs.ProgramAwareTriageUUID,
  deviceUUID: device_uuid,
  experiments: {
    [ExperimentUUIDs.ProgramAwareTriageUUID]: {
      assignment: [
        {
          arm,
          joinKeys: {
            client_lyra_id,
            toolkit_topic: selectedToolkitTopic,
          },
        },
      ],
    },
  },
})

export const generateGenericExperimentPayload = ({
  experimentUUID,
  deviceUUID,
  joinKeys,
  platform,
  flagName,
  flagValue,
}: GenericExperimentPayloadParams): ExperimentPayload => ({
  experimentUUID,
  deviceUUID,
  experiments: {
    [experimentUUID]: {
      assignment: [
        {
          joinKeys,
          platform,
          flagName,
          flagValue,
        },
      ],
    },
  },
})
